// Shared helpers ported from qa-live/src/app/pulse/page.tsx.

import { PODS, SPRINT_END_DATES } from './pulseConfig.js'

export function sumKey(arr, key) {
    return arr.reduce((acc, item) => acc + (Number(item[key]) || 0), 0)
}

// A sprint is "valid" (worth showing) if its previous sprint's end date has passed.
// This filters out future sprints that exist in Jira but haven't started yet.
export function isValidSprint(sprintNumber) {
    const today = new Date().toISOString().slice(0, 10)
    if (!SPRINT_END_DATES[sprintNumber]) return false
    const startDate = SPRINT_END_DATES[sprintNumber - 1]
    if (startDate) return startDate <= today
    return true
}

// Combine sprint data across pods. When `selectedPod` is 'all', sprints with the
// same number across pods are merged into one row. Hotfix/patch releases are deduped
// by detail string since CR releases are shared across pods (sum-counting would 3x).
export function aggregateSprints(pods, selectedPod) {
    const targetPods = selectedPod === 'all' ? pods : pods.filter((p) => p.podName === selectedPod)
    if (targetPods.length === 0) return []

    if (targetPods.length === 1) {
        return targetPods[0].sprints
            .filter((s) => isValidSprint(s.sprintNumber))
            .map((s) => ({
                label: `S${s.sprintNumber}`,
                sprintNumber: s.sprintNumber,
                startDate: s.startDate ?? '',
                endDate: s.endDate ?? '',
                completedPoints: s.completedPoints,
                committedPoints: s.committedPoints,
                aiCompletedPoints: s.aiCompletedPoints,
                aiCompletedCount: s.aiCompletedCount,
                traditionalPoints: s.completedPoints - s.aiCompletedPoints,
                rolloverCount: s.rolloverCount,
                activeDevs: s.activeDevs,
                ptsPerDev: s.ptsPerDev,
                committedCount: s.committedCount,
                completedCount: s.completedCount,
                avgStorySize: s.avgStorySize,
                bugsInSprint: s.bugsInSprint,
                bugsCompleted: s.bugsCompleted,
                p0Count: s.p0Count,
                p1Count: s.p1Count,
                p2Count: s.p2Count,
                hotfixCount: s.hotfixCount,
                hotfixDetails: s.hotfixDetails,
                patchCount: s.patchCount,
                patchDetails: s.patchDetails,
                scheduledCount: s.scheduledCount,
                releaseCount: s.releaseCount,
                qaActiveCount: s.qaActiveCount,
                avgCycleTimeDays: s.avgCycleTimeDays,
                excludedCount: s.excludedCount,
                developerStats: s.developerStats,
                state: s.state,
            }))
    }

    const allSprintNumbers = Array.from(
        new Set(
            targetPods.flatMap((p) => p.sprints.filter((s) => isValidSprint(s.sprintNumber)).map((s) => s.sprintNumber))
        )
    ).sort((a, b) => a - b)

    return allSprintNumbers
        .map((num) => {
            const matching = targetPods.flatMap((p) => p.sprints.filter((s) => s.sprintNumber === num))
            if (matching.length === 0) return null

            const completedPoints = sumKey(matching, 'completedPoints')
            const activeDevs = sumKey(matching, 'activeDevs')

            const devMap = new Map()
            for (const sprint of matching) {
                for (const dev of sprint.developerStats) {
                    const existing = devMap.get(dev.name) ?? {
                        name: dev.name,
                        completedPoints: 0,
                        aiPoints: 0,
                        issueCount: 0,
                        bugsOpened: 0,
                        bugsClosed: 0,
                        bugsOpenedByPriority: {},
                    }
                    existing.completedPoints += dev.completedPoints
                    existing.aiPoints += dev.aiPoints
                    existing.issueCount += dev.issueCount
                    existing.bugsOpened += dev.bugsOpened
                    existing.bugsClosed += dev.bugsClosed
                    for (const [p, count] of Object.entries(dev.bugsOpenedByPriority ?? {})) {
                        existing.bugsOpenedByPriority[p] = (existing.bugsOpenedByPriority[p] ?? 0) + (count ?? 0)
                    }
                    devMap.set(dev.name, existing)
                }
            }

            const aiCompletedPoints = sumKey(matching, 'aiCompletedPoints')
            const avgStorySize =
                matching.length > 0
                    ? Math.round((matching.reduce((a, s) => a + s.avgStorySize, 0) / matching.length) * 10) / 10
                    : 0

            return {
                label: `S${num}`,
                sprintNumber: num,
                startDate:
                    matching
                        .map((s) => s.startDate)
                        .filter(Boolean)
                        .sort()
                        .shift() ?? '',
                endDate:
                    matching
                        .map((s) => s.endDate)
                        .filter(Boolean)
                        .sort()
                        .pop() ?? '',
                completedPoints,
                committedPoints: sumKey(matching, 'committedPoints'),
                aiCompletedPoints,
                aiCompletedCount: sumKey(matching, 'aiCompletedCount'),
                traditionalPoints: completedPoints - aiCompletedPoints,
                rolloverCount: sumKey(matching, 'rolloverCount'),
                activeDevs,
                ptsPerDev: activeDevs > 0 ? Math.round(completedPoints / activeDevs) : 0,
                committedCount: sumKey(matching, 'committedCount'),
                completedCount: sumKey(matching, 'completedCount'),
                avgStorySize,
                bugsInSprint: sumKey(matching, 'bugsInSprint'),
                bugsCompleted: sumKey(matching, 'bugsCompleted'),
                p0Count: sumKey(matching, 'p0Count'),
                p1Count: sumKey(matching, 'p1Count'),
                p2Count: sumKey(matching, 'p2Count'),
                // CR releases are shared across pods — dedupe by detail string before counting
                hotfixDetails: Array.from(new Set(matching.flatMap((s) => s.hotfixDetails))),
                hotfixCount: Array.from(new Set(matching.flatMap((s) => s.hotfixDetails))).length,
                patchDetails: Array.from(new Set(matching.flatMap((s) => s.patchDetails))),
                patchCount: Array.from(new Set(matching.flatMap((s) => s.patchDetails))).length,
                scheduledCount: Math.max(...matching.map((s) => s.scheduledCount)),
                releaseCount: Math.max(...matching.map((s) => s.releaseCount)),
                qaActiveCount: sumKey(matching, 'qaActiveCount'),
                avgCycleTimeDays:
                    matching.length > 0
                        ? Math.round((matching.reduce((a, s) => a + s.avgCycleTimeDays, 0) / matching.length) * 10) / 10
                        : 0,
                excludedCount: sumKey(matching, 'excludedCount'),
                developerStats: Array.from(devMap.values()).sort((a, b) => b.completedPoints - a.completedPoints),
                state: matching.some((s) => s.state === 'active') ? 'active' : 'closed',
            }
        })
        .filter(Boolean)
}

// Map a "Buyer"/"Seller"/"Foundations" display name → its project key (BP/SP/FP).
export function podNameToKey(name) {
    const found = Object.values(PODS).find((p) => p.name === name)
    return found?.key
}

// Avatar initials from "Firstname Lastname" → "FL".
export function getInitials(name) {
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Deterministic color hash for member avatars.
export function colorFromName(name, palette) {
    let hash = 0
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0
    return palette[hash % palette.length]
}
